import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Alert,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginIcon from '@mui/icons-material/Login';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';

const TrackingTransparency: React.FC = () => {
  const { currentUser } = useAuth();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {!currentUser && (
        <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'primary.dark' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" color="white">
              Download our iOS app to start tracking your vision health
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              color="secondary"
              startIcon={<LoginIcon />}
              sx={{ 
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'secondary.light'
                }
              }}
            >
              Sign In
            </Button>
          </Box>
        </Paper>
      )}

      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <PhoneAndroidIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h3" component="h1">
            App Tracking Transparency
          </Typography>
        </Box>
        
        <Typography variant="h6" color="text.secondary" paragraph>
          iOS App Privacy & User Control
        </Typography>

        <Alert severity="info" sx={{ my: 3 }}>
          <Typography variant="body2">
            <strong>Last updated:</strong> February 4, 2026
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            This page explains how our iOS app uses Apple's App Tracking Transparency framework and what data we collect.
          </Typography>
        </Alert>

        <Divider sx={{ my: 4 }} />

        {/* What is ATT */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            What is App Tracking Transparency?
          </Typography>
          <Typography variant="body1" paragraph>
            App Tracking Transparency (ATT) is Apple's privacy framework that requires apps to ask your permission before tracking your activity across other companies' apps and websites.
          </Typography>
          <Typography variant="body1" paragraph>
            Starting with iOS 14.5, all apps must show you a prompt asking for permission to track you. You have full control over this decision.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* What We Track */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="primary" />
            What Tracking Means in Our App
          </Typography>
          <Typography variant="body1" paragraph>
            When you allow tracking in Amsler Grid Daily, we use your Apple Advertising ID (IDFA) to show you more relevant ads through Google AdMob.
          </Typography>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: '#c8e6c9' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                    <Typography variant="h6" component="div" sx={{ color: '#1b5e20', fontWeight: 'bold' }}>
                      If You Allow Tracking
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <AdsClickIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Personalized ads"
                        secondary="Ads that match your interests"
                        primaryTypographyProps={{ sx: { color: '#1b5e20', fontWeight: 'bold' } }}
                        secondaryTypographyProps={{ sx: { color: '#2e7d32' } }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <MonetizationOnIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Earn tokens normally"
                        secondary="Watch ads, earn tokens"
                        primaryTypographyProps={{ sx: { color: '#1b5e20', fontWeight: 'bold' } }}
                        secondaryTypographyProps={{ sx: { color: '#2e7d32' } }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Support the app"
                        secondary="Higher ad revenue helps us"
                        primaryTypographyProps={{ sx: { color: '#1b5e20', fontWeight: 'bold' } }}
                        secondaryTypographyProps={{ sx: { color: '#2e7d32' } }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', bgcolor: '#ffe0b2' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CancelIcon sx={{ color: '#e65100' }} />
                    <Typography variant="h6" component="div" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                      If You Deny Tracking
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <AdsClickIcon fontSize="small" sx={{ color: '#e65100' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Contextual ads only"
                        secondary="Generic ads shown"
                        primaryTypographyProps={{ sx: { color: '#e65100', fontWeight: 'bold' } }}
                        secondaryTypographyProps={{ sx: { color: '#f57c00' } }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <MonetizationOnIcon fontSize="small" sx={{ color: '#e65100' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Still earn tokens"
                        secondary="All features work the same"
                        primaryTypographyProps={{ sx: { color: '#e65100', fontWeight: 'bold' } }}
                        secondaryTypographyProps={{ sx: { color: '#f57c00' } }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon fontSize="small" sx={{ color: '#e65100' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="More privacy"
                        secondary="Less data shared"
                        primaryTypographyProps={{ sx: { color: '#e65100', fontWeight: 'bold' } }}
                        secondaryTypographyProps={{ sx: { color: '#f57c00' } }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="bold">
              ✅ All app features work the same regardless of your tracking choice!
            </Typography>
          </Alert>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Data We Collect */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DevicesIcon color="primary" />
            Data Collection Details
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            When Tracking is ALLOWED:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Apple Advertising ID (IDFA)"
                secondary="Used by Google AdMob to show personalized ads"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Ad Interaction Data"
                secondary="Which ads you view, click, or watch to completion"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Device Information"
                secondary="Device model, OS version, screen size (for ad compatibility)"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            When Tracking is DENIED:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CancelIcon color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="No IDFA Access"
                secondary="We cannot access your advertising identifier"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Basic Ad Metrics Only"
                secondary="Generic ad impressions (not personalized)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="App Analytics"
                secondary="Anonymous usage data (not linked to advertising)"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Data We NEVER Collect */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            What We DON'T Track
          </Typography>
          
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              Your vision test data NEVER leaves your device!
            </Typography>
          </Alert>

          <Typography variant="body1" paragraph>
            Regardless of your tracking choice, we <strong>NEVER</strong> collect or transmit:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <CancelIcon sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Vision Test Results"
                secondary="All test data is stored locally on your device only"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CancelIcon sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Test Notes"
                secondary="Your personal notes stay on your device"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CancelIcon sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Health Information"
                secondary="No medical or health data is transmitted"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CancelIcon sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Personal Identity"
                secondary="No name, email, or contact information (unless you use premium features)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CancelIcon sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Location Data"
                secondary="We don't track where you are"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* User Control */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            Your Control & Privacy Rights
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            In the App:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="During Onboarding"
                secondary="You'll see an education screen explaining tracking before the iOS prompt"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="In Settings"
                secondary="View your current tracking status and learn more anytime"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="iOS Settings"
                secondary="Change tracking permission in Settings > Privacy > Tracking"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Change Your Mind Anytime:
          </Typography>
          <Card variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5', border: '1px solid #ddd' }}>
            <Typography variant="body2" paragraph sx={{ color: '#000', fontWeight: 'bold' }}>
              <strong>To change tracking permission:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ color: '#333' }}>
              1. Open iOS <strong>Settings</strong><br />
              2. Scroll down to <strong>Amsler Grid Daily</strong><br />
              3. Tap <strong>Allow [App] to Track</strong><br />
              4. Toggle ON or OFF
            </Typography>
          </Card>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Third Party Partners */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            Third-Party Partners
          </Typography>
          
          <Typography variant="body1" paragraph>
            When you allow tracking, we work with these partners:
          </Typography>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Google AdMob (Google LLC)
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Purpose:</strong> Serve ads and reward you with tokens
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Data Collected:</strong> IDFA, ad interactions, device info
              </Typography>
              <Typography variant="body2">
                <strong>Privacy Policy:</strong>{' '}
                <a 
                  href="https://policies.google.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2' }}
                >
                  https://policies.google.com/privacy
                </a>
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Firebase (Google LLC)
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Purpose:</strong> Analytics, token balance sync, app functionality
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Data Collected:</strong> Anonymous user ID, app usage data (not linked to advertising)
              </Typography>
              <Typography variant="body2">
                <strong>Privacy Policy:</strong>{' '}
                <a 
                  href="https://firebase.google.com/support/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2' }}
                >
                  https://firebase.google.com/support/privacy
                </a>
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Apple Requirements */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            Apple's App Store Requirements
          </Typography>
          
          <Typography variant="body1" paragraph>
            Our app complies with Apple's App Tracking Transparency framework:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Clear Explanation"
                secondary="We explain why we want to track you in simple terms"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="User Choice"
                secondary="You have full control - all features work either way"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="No Incentives"
                secondary="We don't offer rewards for allowing tracking"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Respect Your Decision"
                secondary="We honor your choice and don't ask repeatedly"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Summary */}
        <Box sx={{ my: 4 }}>
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Your Choice:</strong> You decide if we can track you for ads
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>All Features Work:</strong> The app works the same either way
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Test Data Private:</strong> Vision tests never leave your device
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Change Anytime:</strong> Update your choice in iOS Settings
            </Typography>
            <Typography variant="body2">
              • <strong>Transparent:</strong> We're open about what we track and why
            </Typography>
          </Alert>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Contact */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            Questions or Concerns?
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about our tracking practices or want to exercise your privacy rights:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="View our full Privacy Policy"
                secondary={
                  <Button 
                    component={RouterLink} 
                    to="/privacy" 
                    size="small"
                    sx={{ pl: 0 }}
                  >
                    Go to Privacy Policy →
                  </Button>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Read our Terms of Service"
                secondary={
                  <Button 
                    component={RouterLink} 
                    to="/terms" 
                    size="small"
                    sx={{ pl: 0 }}
                  >
                    Go to Terms of Service →
                  </Button>
                }
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ bgcolor: '#e0e0e0', p: 3, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#000', fontWeight: 'bold' }}>
            <strong>Amsler Grid Daily</strong> | App Tracking Transparency Declaration
          </Typography>
          <Typography variant="body2" sx={{ color: '#333', mt: 1 }}>
            Last Updated: February 4, 2026 | Version 1.3
          </Typography>
          <Typography variant="body2" sx={{ color: '#333', mt: 1 }}>
            For iOS 14.5 and later
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TrackingTransparency;
